import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { IntegrityCategory, WhistleblowerLookup, WhistleblowerSubmitted } from '../../lib/types';
import { ErrorNote, Field, Tabs } from '../../ui/components';

const CATEGORIES: IntegrityCategory[] = ['BRIBERY', 'ABUSE_OF_OFFICE', 'PROCUREMENT_FRAUD', 'ASSET_ILLICIT_ENRICHMENT', 'EMBEZZLEMENT', 'CONFLICT_OF_INTEREST', 'OTHER_CORRUPTION'];

/**
 * Genuinely public, unauthenticated — POST /api/v1/m40/whistleblower/reports
 * and /whistleblower/reports/lookup are mounted before requireAuth in
 * m40-ombudsman/src/app.ts specifically so this works with zero SeyID
 * session ("Tor-friendly public form" per the module's own design notes).
 * No bearer token is sent (lib/api.ts only attaches one if present in
 * localStorage — an anonymous visitor to this route has none).
 */
export function PublicWhistleblowerIntake() {
  const [tab, setTab] = useState<'submit' | 'check'>('submit');
  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-1 text-xl font-bold text-slate-900">Report Corruption — Anti-Corruption Commission of Seychelles</h1>
      <p className="mb-6 text-sm text-slate-500">
        Anonymous by design. We never ask for your name, phone, email, or NIN — nothing that identifies you is collected or stored.
      </p>
      <Tabs tabs={[{ key: 'submit', label: 'Submit a report' }, { key: 'check', label: 'Check a report' }]} value={tab} onChange={setTab} />
      {tab === 'submit' ? <SubmitPanel /> : <CheckPanel />}
    </div>
  );
}

function SubmitPanel() {
  const [category, setCategory] = useState<IntegrityCategory>('BRIBERY');
  const [subjectMda, setSubjectMda] = useState('');
  const [subjectDescription, setSubjectDescription] = useState('');
  const [narrative, setNarrative] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<WhistleblowerSubmitted | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<WhistleblowerSubmitted>('/api/v1/m40/whistleblower/reports', {
        category,
        subjectMda: subjectMda || undefined,
        subjectDescription,
        narrative,
        evidence: [],
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Submission failed — please try again in a minute');
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="card border-amber-300 bg-amber-50 p-5">
        <h2 className="mb-2 text-base font-bold text-amber-900">Report received — reference {result.refNo}</h2>
        <p className="mb-3 text-sm text-amber-900">{result.warning}</p>
        <div className="rounded-lg border border-amber-300 bg-white p-3 font-mono text-sm text-amber-950">{result.caseKey}</div>
        <p className="mt-3 text-xs text-amber-800">
          Save both the reference and this case key somewhere safe. Use the &ldquo;Check a report&rdquo; tab any time to see its status — there is no
          other way to look it up, and we cannot recover a lost key.
        </p>
      </div>
    );
  }

  const valid = subjectDescription.trim().length >= 5 && narrative.trim().length >= 20;

  return (
    <div className="card space-y-3 p-5">
      {error ? <ErrorNote message={error} /> : null}
      <Field label="Category">
        <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as IntegrityCategory)}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Subject MDA (optional — which ministry/department/agency)">
        <input className="field-input" value={subjectMda} onChange={(e) => setSubjectMda(e.target.value)} />
      </Field>
      <Field label="Describe the alleged wrongdoer/entity (never yourself)">
        <input className="field-input" value={subjectDescription} onChange={(e) => setSubjectDescription(e.target.value)} placeholder="e.g. A procurement officer at the Ministry of X" />
      </Field>
      <Field label="What happened">
        <textarea className="field-input" rows={5} value={narrative} onChange={(e) => setNarrative(e.target.value)} placeholder="Dates, amounts, who was involved, any evidence you can describe…" />
      </Field>
      <div className="flex justify-end pt-2">
        <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
          {busy ? 'Submitting…' : 'Submit anonymously'}
        </button>
      </div>
    </div>
  );
}

function CheckPanel() {
  const [refNo, setRefNo] = useState('');
  const [caseKey, setCaseKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<WhistleblowerLookup | null>(null);

  const check = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.post<WhistleblowerLookup>('/api/v1/m40/whistleblower/reports/lookup', { refNo, caseKey });
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No report found for that reference and case key');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card space-y-3 p-5">
      {error ? <ErrorNote message={error} /> : null}
      <Field label="Reference (e.g. WBR-2026-000123)">
        <input className="field-input" value={refNo} onChange={(e) => setRefNo(e.target.value)} />
      </Field>
      <Field label="Case key">
        <input className="field-input font-mono" value={caseKey} onChange={(e) => setCaseKey(e.target.value)} />
      </Field>
      <div className="flex justify-end pt-2">
        <button className="btn-primary" onClick={check} disabled={busy || !refNo.trim() || !caseKey.trim()}>
          {busy ? 'Checking…' : 'Check status'}
        </button>
      </div>
      {result ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          Status: <span className="font-bold">{result.status.replaceAll('_', ' ')}</span>
          {result.triageNote ? <div className="mt-1 text-xs">{result.triageNote}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
